/**
 * The page's AdSense connection, for units `boot()` renders with
 * `demand: "adsense"` (ADR-032).
 *
 * AdSense is not an auction this page runs: there is no GPT slot, no Prebid,
 * no price. The page loads Google's `adsbygoogle.js` once, places one
 * `<ins class="adsbygoogle">` per placement, pushes it once, and then reads
 * the answer Google writes back onto the element (`data-ad-status`).
 *
 * What this owns, so the mount code does not have to:
 *   - the consent gate (the same `resolveConsent()` the GAM path awaits before
 *     its first request; non-affirmative consent asks Google for
 *     non-personalised ads, the AdSense equivalent of GAM's `npa=1`),
 *   - loading `adsbygoogle.js` once per page, and never when the publisher
 *     already runs AdSense on the page,
 *   - the one `push({})` per `<ins>`,
 *   - turning `data-ad-status` (or its absence) into filled / unfilled.
 *
 * Never refreshes: AdSense policy forbids programmatic refresh, so nothing here
 * pushes an `<ins>` twice. Never throws into the page.
 *
 * Imports nothing from `widgets/`, `protocol/` or `api/`.
 */
import { resolveConsent } from './ConsentGate.js';

export const ADSENSE_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

/** How long Google may take to answer a pushed `<ins>` before it counts as empty. */
export const ADSENSE_STATUS_TIMEOUT_MS = 10000;

const CLIENT_PATTERN = /^ca-pub-\d{10,20}$/;
const SLOT_PATTERN = /^\d{1,20}$/;

const swallow = (fn) => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

/** A unit's `adsense` block is usable: a publisher id and a numeric ad slot. */
export function isAdSenseConfig(adsense) {
  return (
    typeof adsense?.client === 'string' &&
    CLIENT_PATTERN.test(adsense.client) &&
    typeof adsense?.slot === 'string' &&
    SLOT_PATTERN.test(adsense.slot)
  );
}

/**
 * The `<ins>` AdSense fills, built with DOM APIs only - config values never
 * pass through innerHTML.
 */
export function createAdSenseIns(doc, { id, client, slot, size: [width, height] }) {
  const ins = doc.createElement('ins');
  if (id) ins.id = id;
  ins.className = 'adsbygoogle';
  ins.setAttribute('style', `display:inline-block;width:${width}px;height:${height}px`);
  ins.setAttribute('data-ad-client', client);
  ins.setAttribute('data-ad-slot', slot);
  return ins;
}

function adSenseScriptPresent(doc) {
  try {
    return Array.from(doc.scripts ?? []).some((s) => (s.src || '').includes('adsbygoogle.js'));
  } catch {
    return false;
  }
}

export class AdSensePage {
  #config;
  #win;
  #doc;
  #timeoutMs;
  #onDiagnostic;
  #consentPromise = null;

  /**
   * @param {object} opts
   * @param {object} opts.config      The sdk-config payload (reads `consent`).
   * @param {Window} [opts.win]
   * @param {number} [opts.timeoutMs] Wait for `data-ad-status` before calling it empty.
   * @param {(msg: string, detail?: unknown) => void} [opts.onDiagnostic]
   */
  constructor({
    config,
    win = typeof window !== 'undefined' ? window : undefined,
    timeoutMs = ADSENSE_STATUS_TIMEOUT_MS,
    onDiagnostic,
  } = {}) {
    this.#config = config ?? {};
    this.#win = win;
    this.#doc = win?.document;
    this.#timeoutMs = timeoutMs;
    this.#onDiagnostic = onDiagnostic ?? (() => {});
  }

  /**
   * Request one already-placed `<ins>` (see createAdSenseIns()).
   *
   * Exactly one of `onFilled` / `onUnfilled` runs, at most once: `onFilled`
   * when Google answers `filled`; `onUnfilled` on `unfilled`, on no answer
   * within the timeout, or when the push itself fails.
   *
   * @returns {() => void} stop - releases the observer and timer; neither
   *   callback runs afterwards. Idempotent.
   */
  fill(ins, { client, onFilled, onUnfilled } = {}) {
    let done = false;
    let observer = null;
    let timer = null;
    const release = () => {
      done = true;
      swallow(() => observer?.disconnect());
      if (timer !== null) clearTimeout(timer);
      observer = null;
      timer = null;
    };
    const finish = (filled, reason) => {
      if (done) return;
      release();
      swallow(() => (filled ? onFilled?.() : onUnfilled?.(reason)));
    };

    this.#consent()
      .then((consent) => {
        if (done) return;
        const win = this.#win;
        if (!win || !this.#doc || !ins) return finish(false, 'no page');
        this.#ensureScript(client);

        const read = () => {
          const status = ins.getAttribute('data-ad-status');
          if (status === 'filled') finish(true);
          else if (status === 'unfilled') finish(false, 'unfilled');
        };
        if (typeof win.MutationObserver === 'function') {
          observer = swallow(() => new win.MutationObserver(read));
          swallow(() =>
            observer?.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] }),
          );
        }
        timer = setTimeout(() => finish(false, 'timeout'), this.#timeoutMs);

        try {
          win.adsbygoogle = win.adsbygoogle || [];
          // The AdSense equivalent of GAM's npa=1: whenever consent is not
          // affirmative, ask for non-personalised ads rather than none.
          if (consent?.npa) win.adsbygoogle.requestNonPersonalizedAds = 1;
          win.adsbygoogle.push({});
        } catch (error) {
          this.#onDiagnostic('adsbygoogle.push failed', String(error));
          finish(false, 'push failed');
          return;
        }
        // Google may have answered synchronously (a stubbed or cached library).
        read();
      })
      .catch((error) => {
        this.#onDiagnostic('adsense fill failed', String(error));
        finish(false, 'error');
      });

    return release;
  }

  #consent() {
    if (!this.#consentPromise) {
      this.#consentPromise = Promise.resolve(
        swallow(() =>
          resolveConsent({
            mode: this.#config?.consent?.mode ?? 'required',
            timeoutMs: this.#config?.consent?.timeoutMs,
            win: this.#win,
          }),
        ),
      ).catch(() => undefined);
    }
    return this.#consentPromise;
  }

  /** One `adsbygoogle.js` per page, the publisher's own copy included. */
  #ensureScript(client) {
    const doc = this.#doc;
    if (adSenseScriptPresent(doc)) return;
    try {
      const el = doc.createElement('script');
      el.async = true;
      el.crossOrigin = 'anonymous';
      el.src = `${ADSENSE_SRC}?client=${encodeURIComponent(client)}`;
      el.onerror = () => this.#onDiagnostic('adsbygoogle.js failed to load');
      (doc.head || doc.documentElement).appendChild(el);
    } catch (error) {
      this.#onDiagnostic('adsbygoogle.js not injected', String(error));
    }
  }
}
